import { css } from "@linaria/core";
import React, { memo, useCallback } from "react";
import t from "../../../../../../../../../i18n";
import Switch from "@ui/Switch";
import { colors, fonts, spacings } from "@ui/Theme";
import type { BlockDiagramModelT } from "../../../../../../schema/block/diagram";
import Select from "@ui/Select";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import TooltipV2 from "@ui/TooltipV2";
import { InformationLine } from "@ui/IconV2/react";

export const AnnotationLayerEvolutionsSettings = memo(
  ({
    block,
    annotation,
    encodingOptions,
  }: {
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
    encodingOptions: { label: string; value: string }[];
  }) => {
    const editor = useDashboardEditorContext();
    const enabled = annotation.evolutions.length > 0;
    const identifier = annotation.encodings[0]?.identifier;
    const values = annotation.evolutions.map(({ column, quantity }) =>
      quantity ? `${column}/${quantity}` : column,
    );

    const options = encodingOptions.filter(o =>
      annotation?.encodings.some(e => {
        if (
          e.column === "quantityImpacts" ||
          e.column === "systemImpact" ||
          e.column === "externalImpacts" ||
          e.column === "unexplainedLosses" ||
          e.column === "noSystemImpacts"
        )
          return `${e.column}/${e.quantity}` === o.value;
        return e.column === o.value;
      }),
    );

    const toggle = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!identifier) return;
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(l => {
              if (l !== annotation) return l;
              return {
                ...l,
                evolutions: e.target.checked
                  ? options.reduce<
                      { identifier: string; column: string; quantity: string | null }[]
                    >((acc, o) => {
                      const [column, quantity = null] = o.value.split("/");
                      return column ? [...acc, { identifier, column, quantity }] : acc;
                    }, [])
                  : [],
              };
            }),
          },
        });
      },
      [annotation, block, editor, options, identifier],
    );

    const update = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (!identifier) return;

        const values = Array.from(e.currentTarget.selectedOptions).reduce<
          { identifier: string; column: string; quantity: string | null }[]
        >((acc, option) => {
          const [column, quantity = null] = option.value.split("/");
          if (!column || !identifier) return acc;
          return [...acc, { identifier, column, quantity }];
        }, []);

        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(l => {
              if (l !== annotation) return l;
              return {
                ...l,
                evolutions: values,
              };
            }),
          },
        });
      },
      [annotation, block, editor, identifier],
    );

    const disabledEvolutionSetting = Boolean(block.config.aggregate);

    return (
      <div className={annotationEvolutionSettings}>
        <Switch
          position="right"
          justify
          checked={enabled}
          onChange={toggle}
          disabled={disabledEvolutionSetting}
        >
          <span className={annotationEvolutionSettingsTitle}>
            {t("project.dashboard.editor.gui.section.evolution.title")}
            <TooltipV2 overlay={<EvolutionExplanations />}>
              <span>
                <InformationLine color={colors.carbon800} />
              </span>
            </TooltipV2>
          </span>
        </Switch>
        {enabled && (
          <Select
            label={t("project.dashboard.editor.gui.section.annotation_settings.assigned_threshold")}
            options={options}
            revertable={false}
            value={values}
            onChange={update}
            prompt=""
            multiple
            clearable={false}
            disabled={disabledEvolutionSetting}
          />
        )}
      </div>
    );
  },
);

const EvolutionExplanations = () => (
  <div className={evolutionExplanations}>
    <p>{t("project.dashboard.block.diagram.evolution.detail")}</p>
    <ul>
      <li>{t("project.dashboard.block.diagram.evolution.detail.up")}</li>
      <li>{t("project.dashboard.block.diagram.evolution.detail.down")}</li>
    </ul>
    <p>{t("project.dashboard.block.diagram.evolution.detail.computation")}</p>
  </div>
);

const annotationEvolutionSettings = css`
  margin-top: ${spacings.m};

  > * + * {
    margin-top: ${spacings.xs};
  }

  &,
  > * {
    width: 100%;
  }
`;

const annotationEvolutionSettingsTitle = css`
  svg {
    transform: translateY(2px);
    margin-left: ${spacings.xxs};
  }
`;

const evolutionExplanations = css`
  max-width: 320px;
  p,
  ul {
    font: ${fonts.body2};
    margin: 0;
  }
  ul {
    list-style: outside;
    padding-left: 1.5em;
  }
`;
